<template>
  <div>
    <div class="home" v-if="isMessagesLoaded && isDataLoaded">
      <div class="context-bar">
        <div class="navigation-tabs-container">
          <navigation-tabs v-if="displayMainTabs" :tabs="navigationTabs" />
        </div>
        <div
          class="create-btn-wrapper"
          v-if="isListPage && !failedToLoadContent"
        >
          <b-dropdown
            split
            size="sm"
            toggle-class="custom-dropdown"
            variant="none"
            class="btn-grp"
            right
          >
            <template #button-content>
              <button class="nt-btn" v-b-modal.create-wizard>
                {{ $t("newPage") }}
              </button>
              <create-app-wizard></create-app-wizard>
            </template>

            <b-dropdown-item-button v-if="hasTemplates" v-b-modal="'new-cft-'">
              {{ $t("newPageFromPageTemplate") }}
            </b-dropdown-item-button>
            <new-content-from-template-modal
              :templates="templates"
            ></new-content-from-template-modal>

            <b-dropdown-item-button v-b-modal="'create-cff'">
              {{ $t("newPageFromFile") }}
            </b-dropdown-item-button>
            <new-content-from-file-modal></new-content-from-file-modal>

            <b-dropdown-item-button v-b-modal="'create-ccf'">
              {{ $t("newCustomPageFromFile") }}
            </b-dropdown-item-button>
            <new-custom-content-from-file-modal></new-custom-content-from-file-modal>

            <b-dropdown-item-button v-b-modal="'import-cc'">
              {{ $t("importCustomPageFromCRM") }}
            </b-dropdown-item-button>
            <import-custom-content-modal></import-custom-content-modal>
          </b-dropdown>
        </div>
        <div
          class="create-btn-wrapper"
          v-if="isTemplateList && !failedToLoadContent"
        >
          <b-dropdown
            split
            right
            size="sm"
            toggle-class="custom-dropdown"
            variant="none"
            class="btn-grp"
          >
            <template #button-content>
              <button class="nt-btn" v-b-modal.create-wizard>
                {{ $t("newPageTemplate") }}
              </button>
              <create-app-wizard :as-template="true"></create-app-wizard>
            </template>

            <b-dropdown-item-button v-b-modal="'import-template-'">
              {{ $t("newPageTemplateFromFile") }}
            </b-dropdown-item-button>
            <import-template-modal></import-template-modal>
          </b-dropdown>
        </div>
        <div class="org-info" v-if="hasOrgInfo">
          <div class="org-name">{{ org.Name || "ORGNAME" }}</div>
          <div class="is-sandbox" v-if="org.isSandbox">
            {{ getMessageText(messages.SANDBOX) }}
          </div>
          <div class="is-sandbox" v-else>
            {{ getMessageText(messages.Production) }}
          </div>
        </div>

        <div class="org-info" v-else>
          <div class="org-name">
            {{ getMessageText(messages.MISSING_VIEW_SETUP) }}
          </div>
        </div>
        <div class="un-wrapper">
          <div class="un">{{ username }}</div>
          <div class="sfdc-un">{{ user ? user.sfdcUsername : "USERNAME" }}</div>
        </div>
        <div class="logout-btn-wrapper">
          <button class="logout-btn" @click="logout()">
            {{ getMessageText(messages.SIGN_OUT) }}
          </button>
        </div>
      </div>

      <v-bread-crumb v-if="!displayMainTabs"></v-bread-crumb>

      <router-view></router-view>

      <transition name="loader">
        <div class="loader-wrapper" v-if="displayLoader">
          <div class="loader"></div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
  import { mapState } from "vuex";
  import {
    ROUTE_NAMES,
    MUTATION_NAME,
    TOAST_OPTIONS
  } from "@/static/global-variables";
  import CreateAppWizard from "@/components/CreateAppWizard";
  import VBreadCrumb from "@/components/VBreadCrumb.vue";
  import NavigationTabs from "@/components/NavigationTabs";
  import services from "@/services";
  import DistributionChannelsService from "@/services/distributionChannelsService";
  import ImportTemplateModal from "@/components/ImportTemplateModal";
  import NewContentFromTemplateModal from "@/components/NewContentFromTemplateModal";
  import NewContentFromFileModal from "@/components/NewContentFromFileModal";
  import NewCustomContentFromFileModal from "@/components/NewCustomContentFromFileModal.vue";
  import ImportCustomContentModal from "@/components/ImportCustomContentModal.vue";

  export default {
    name: "Home",
    components: {
      ImportCustomContentModal,
      NewCustomContentFromFileModal,
      ImportTemplateModal,
      CreateAppWizard,
      VBreadCrumb,
      NavigationTabs,
      NewContentFromTemplateModal,
      NewContentFromFileModal
    },
    data() {
      return {
        displayLoader: false,
        errorLoadingContent: this.$route.params.errorLoadingContent
      };
    },
    computed: {
      username: function() {
        return this.$cookies.get("username");
      },
      currentRoute() {
        return this.$route.name;
      },
      isMessagesLoaded() {
        return Boolean(this.messages);
      },
      isDataLoaded() {
        return Boolean(this.apps && this.org);
      },
      isListPage() {
        return this.currentRoute === ROUTE_NAMES.appsList;
      },
      isTemplateList() {
        return this.$route.name === ROUTE_NAMES.templatesList;
      },
      templates() {
        return this.apps.filter(a => !!a.isTemplate);
      },
      hasTemplates() {
        const templates = this.apps.filter(a => !!a.isTemplate);
        return templates.length > 0;
      },
      failedToLoadContent() {
        return this.errorLoadingContent;
      },
      displayMainTabs() {
        return (
          this.currentRoute === ROUTE_NAMES.appsList ||
          this.currentRoute === ROUTE_NAMES.distributionChannels ||
          this.currentRoute === ROUTE_NAMES.templatesList
        );
      },
      distributionChannels() {
        return this.$store.getters[MUTATION_NAME.DISTRIBUTION_CHANNELS_SUMMARY];
      },
      navigationTabs() {
        const tabs = [
          {
            id: "content",
            text: this.$t("page"),
            name: ROUTE_NAMES.appsList
          },
          {
            id: "templates",
            text: this.$t("pageTemplates"),
            path: "/templates",
            name: ROUTE_NAMES.templatesList
          }
        ];
        if (this.distributionChannels && this.distributionChannels.length) {
          tabs.push({
            id: "distributionChannels",
            text: this.$t("distributionChannels"),
            name: ROUTE_NAMES.distributionChannels
          });
        }
        return tabs;
      },
      hasOrgInfo() {
        return this.org.Id || this.org.id;
      },
      ...mapState({
        user: "user",
        org: "org",
        messages: "messages",
        apps: "apps"
      })
    },
    mounted() {
      if (!this.distributionChannels) {
        this.displayLoader = true;
        DistributionChannelsService.distributionChannels()
          .catch(e => {
            this.$bvToast.toast(
              this.getMessageText(this.messages.ERROR_WHILE_LOADING),
              TOAST_OPTIONS.bottomCenter
            );
            console.error(e);
          })
          .finally(() => {
            this.displayLoader = false;
          });
      }
    },
    methods: {
      logout() {
        this.$cookies.remove("sfSession");
        this.$cookies.remove("sfEndpoint");
        this.$cookies.remove("username");
        this.$cookies.remove("JSESSIONID");
        window.location.href = `${services.studioDomain}/login`;
      }
    }
  };
</script>

<style scoped>
  .context-bar {
    display: flex;
    align-items: center;
    height: 3.5rem;
    background-color: rgb(255, 255, 255);
    border-bottom: 1px solid var(--NAV_TABS_BORDER_GREY);
    color: rgb(8, 7, 7);
    padding-left: 1.5rem;
  }

  .create-btn-wrapper {
    margin-left: auto;
    margin-right: 0.5rem;
  }

  .un-wrapper,
  .org-info,
  .logout-btn-wrapper {
    margin-left: 0.5rem;
    margin-right: 0.5rem;
    white-space: nowrap;
  }

  .create-btn,
  .logout-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0;
    background: transparent;
    background-clip: border-box;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    line-height: 1.875rem;
    color: #0070d2;
    -webkit-appearance: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    white-space: nowrap;
  }

  .context-bar div:first-of-type {
    margin-right: auto;
  }

  .navigation-tabs-container {
    flex-basis: 100%;
    height: 100%;
  }
</style>
